import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import useSession from "../hooks/useSession";
import {
  // Color,
  TargetType,
  Transfer,
  TransferStatus,
  VolumeColorTransfer,
  VolumeColorTransferNormalized,
} from "../interfaces/HairTransfer";
import { State } from "../interfaces/State";
import type { AppDispatch } from "../reducers/configureStore";
import { setIsOpenSWW } from "../reducers/global/globalSlice";
import {
  setAppliedImage,
  setGenomeIds,
  setIsNecessaryChange,
  setLoader,
  setSelectedColor,
  setSelectedSubColor,
  setSelectedVolume,
  setShowSmallLoader,
  setStoredPhoto,
  setTransfers,
} from "../reducers/volume/volumeSlice";
import { applyTransfer, getTransfer } from "../services/api/transfer";
import { fetchGetProduct } from "../services/product";
import { colorMapping } from "./ColorMapping";
import { normalizeTransfer } from "./transfer";

export const volumeDataProcessor = (props: {
  setIsLoading: (state: boolean) => void;
  isByColor: boolean | undefined;
}) => {
  const { setIsLoading, isByColor } = props;
  const {
    colors,
    volumes,
    brands,
    transfers,
    selectedColor,
    selectedVolume,
    config,
    selectedSubColor,
    isNecessaryChange,
  } = useSelector((state: State) => state.volume);

  const navigate = useNavigate();
  const { getTransfersResponse, capture } = useSelector(
    (state: State) => state.selfie
  );
  const { genomeIds } = useSelector((state: State) => state.volume);
  const { currentUser } = useSession();
  let interval: any = undefined;
  const selfieId = sessionStorage.getItem("vtoSelfieId");
  const dispatch = useDispatch<AppDispatch>();

  const transferProcessor = (transfer: VolumeColorTransfer) => {
    try {
      if (
        transfer.target_type === TargetType.VolumeColor &&
        transfer.state === "finished" &&
        transfer.status !== TransferStatus.Failed
      ) {
        const normalizedTransfer = normalizeTransfer(transfer);
        const newTransfers = normalizedTransfer.filter((transfer) => {
          return !transfers?.some(
            (existingTransfer) =>
              existingTransfer.color === transfer.color &&
              existingTransfer.volume === transfer.volume
          );
        });
        if (newTransfers.length > 0) {
          dispatch(setTransfers(newTransfers));
          dispatch(setStoredPhoto(transfer.aligned_selfie_url));
        }
        setIsLoading(false);
      }
      if (
        transfer.target_type === TargetType.VolumeColor &&
        transfer.state === "finished" &&
        transfer.status === TransferStatus.Failed
      ) {
        setIsLoading(false);
        document.body.style.overflow = "scroll";
        navigate(-1);
        dispatch(setIsOpenSWW(true));
        dispatch(setLoader(false));
      }
    } catch (error: any) {
      console.log("error: ", error);
    }
  };

  const getTransfers = async () => {
    interval = setInterval(async () => {
      const response = await getTransfer({
        selfieId,
        params: {
          target_type: TargetType.VolumeColor,
          colors: genomeIds,
          volumes: selectedVolume?.name ?? "original",
        },
      });
      const tranfers = response?.data.data;
      tranfers?.map((transfer: VolumeColorTransfer) =>
        transferProcessor(transfer)
      );
      if (
        tranfers?.every(
          (transfer: VolumeColorTransfer) => transfer.state === "finished"
        )
      ) {
        clearInterval(interval);
        interval = undefined;
        dispatch(setShowSmallLoader(false));
        dispatch(setLoader(false));
      }
    }, 2000);
  };

  useEffect(() => {
    if (selfieId && genomeIds) {
      try {
        getTransfers();
        return () => {
          clearInterval(interval);
          interval = undefined;
        };
      } catch (error: any) {
        console.log("error: ", error);
      }
    }
  }, [selfieId, genomeIds, selectedVolume]);

  useEffect(() => {
    const fetchApply = async () => {
      try {
        dispatch(setLoader(true));
        dispatch(setShowSmallLoader(true));
        await applyTransfer({
          selfieId,
          params: {
            target_type: TargetType.VolumeColor,
            colors: genomeIds,
            volumes: selectedVolume.name,
          },
        });
        dispatch(setIsNecessaryChange(true));
      } catch (error: any) {
        console.log("error: ", error);
      }
    };
    if (genomeIds && selfieId && selectedVolume) fetchApply();
  }, [selfieId, genomeIds, selectedVolume]);

  useEffect(() => {
    try {
      const volumeColorTransfer = getTransfersResponse?.find(
        (transfer: Transfer) => transfer.target_type === TargetType.VolumeColor
      );

      if (volumeColorTransfer?.status === TransferStatus.Success) {
        const normalizedTransfer = normalizeTransfer(volumeColorTransfer);
        dispatch(setTransfers(normalizedTransfer));
      }
    } catch (error: any) {
      console.log("error: ", error);
    }
  }, [getTransfersResponse]);

  useEffect(() => {
    try {
      if (isByColor) {
        if (colors?.length) {
          const defaultColor = colors.find((color) => color.position === 1);
          const defaultSubColor = defaultColor?.colors
            ? defaultColor?.colors[0]
            : undefined;
          const genomeIds =
            defaultColor?.colors?.map((color) => color.id).join(",") || "";
          dispatch(setSelectedColor(defaultColor));
          dispatch(setSelectedSubColor(defaultSubColor));
          dispatch(setGenomeIds(genomeIds));
          dispatch(
            setSelectedVolume(
              volumes.find((v) => v.name === "original") ?? volumes[0]
            )
          );
        }
      } else {
        if (brands?.length) {
          const defaultColor = brands[0];
          const defaultSubColor = defaultColor?.colors
            ? defaultColor?.colors[0]
            : undefined;
          const genomeIds =
            defaultColor?.colors?.map((color) => color.id).join(",") || "";
          dispatch(setSelectedColor(defaultColor));
          dispatch(setSelectedSubColor(defaultSubColor));
          dispatch(setGenomeIds(genomeIds));
          dispatch(
            setSelectedVolume(
              volumes.find((v) => v.name === "original") ?? volumes[0]
            )
          );
        }
      }
    } catch (error: any) {
      console.log("error: ", error);
    }
  }, [isByColor, brands, colors]);

  useEffect(() => {
    try {
      let transferToApply: VolumeColorTransferNormalized | undefined =
        undefined;
      if (isNecessaryChange) {
        transferToApply = transfers?.find(
          (transfer) =>
            transfer.color === selectedSubColor?.id &&
            transfer.volume === selectedVolume?.name
        );
        const finishedTransfers = transfers?.every(
          (transfer: VolumeColorTransferNormalized) =>
            transfer.state === "finished"
        );
        if (transferToApply?.image_url && finishedTransfers) {
          dispatch(setShowSmallLoader(false));
          dispatch(setLoader(false));
        }
        if (!transferToApply && finishedTransfers) {
          transferToApply = transfers?.find(
            (transfer) =>
              transfer.volume === selectedVolume?.name && !transfer.color
          );
        }
        dispatch(setAppliedImage(transferToApply?.image_url ?? undefined));
        if (
          transferToApply &&
          transferToApply.volume === selectedVolume.name &&
          selectedColor.colors?.some(
            (color) => color.id === transferToApply?.color
          )
        ) {
          dispatch(setIsNecessaryChange(false));
        }
      }
      if (selectedColor?.name && selectedColor?.name !== "undefined") {
        const mappedColor = colorMapping[selectedColor?.name.toLowerCase()];
        dispatch(
          fetchGetProduct({
            gti: currentUser?.gti ?? "",
            query: `${mappedColor} Color`,
          })
        );
      } else {
        dispatch(
          fetchGetProduct({
            gti: currentUser?.gti ?? "",
            query: "Color",
          })
        );
      }
    } catch (error: any) {
      console.log("error: ", error);
    }
  }, [selectedVolume, selectedSubColor, transfers, selectedColor]);
};

export default volumeDataProcessor;
